// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 201d3dd3-50be-4c3a-ab85-9e2382488e62

// Primordia auth — Cognito via AWS SDK v3 directly (no Amplify).
// Adapted from the aws-transfer adminportal pattern, with one deliberate fix:
// the session gate validates the REAL JWT expiry instead of a sticky flag.

import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  AssociateSoftwareTokenCommand,
  VerifySoftwareTokenCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  ChangePasswordCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { getConfig } from './config';

const KEY = 'primordia.session';
// PKCE material for an in-flight hosted-UI round trip. sessionStorage only, and
// cleared on every exit path (success, failure, sign-out) — a verifier that
// outlives its exchange is a replayable secret.
const PKCE_KEY = 'primordia.pkce';

const client = () => new CognitoIdentityProviderClient({ region: getConfig().region || 'us-east-1' });

// This pool signs in by email with UsernameConfiguration unset, and Cognito's
// default in that case is CASE SENSITIVE. So an account stored as
// `MKatsanes@optimumhit.com` rejected the lowercase its owner naturally typed,
// with "Incorrect username or password" — indistinguishable from a wrong
// password, which is why it cost a real person a real afternoon.
//
// UsernameConfiguration can only be set when the pool is CREATED, so this
// cannot be fixed on the pool without replacing it and every user in it.
// Instead every address is folded to lowercase on the way in, here and at
// user creation in the API, so both sides of the comparison always agree.
// Every stored address was normalised to match.
const normalizeEmail = (e) => String(e || '').trim().toLowerCase();

// Decode a JWT payload (no verification — the API verifies; this is for display
// and expiry checks only).
export function decodeJwt(token) {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(escape(atob(part))));
  } catch (e) {
    return null;
  }
}

function saveSession(authResult, extra = {}) {
  const idToken = authResult.IdToken;
  const claims = decodeJwt(idToken) || {};
  const session = {
    idToken,
    accessToken: authResult.AccessToken,
    refreshToken: authResult.RefreshToken,
    exp: claims.exp ? claims.exp * 1000 : Date.now() + 3600_000,
    email: claims.email || claims['cognito:username'],
    tenantId: claims['custom:companyId'] || null,
    tenantName: claims['custom:companyName'] || null,
    groups: claims['cognito:groups'] || [],
    // Federated sessions must also be torn down at the hosted UI on sign-out.
    sso: !!extra.sso,
  };
  sessionStorage.setItem(KEY, JSON.stringify(session));
  return session;
}

export function getSession() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    // THE FIX: expired token == not authenticated, no sticky flag.
    if (!s.exp || Date.now() >= s.exp) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return s;
  } catch (e) {
    return null;
  }
}

export const isAuthed = () => !!getSession();
export const getIdToken = () => (getSession() || {}).idToken || null;
export const isMaster = () => {
  const s = getSession();
  return !!s && (s.groups || []).includes('OptimumAdmins');
};
// A user with a valid token but no tenant and no group is authenticated and
// entitled to nothing. That is the expected landing state for a federated user
// until an admin assigns them — never paper over it with a default tenant.
export function needsWorkspaceAssignment(session) {
  const s = session || getSession();
  return !!s && !s.tenantId && !(s.groups || []).length;
}

export function signOut() {
  // Read before clearing: we need to know whether this was a federated session.
  let wasSso = false;
  try {
    const raw = sessionStorage.getItem(KEY);
    wasSso = !!raw && !!JSON.parse(raw).sso;
  } catch (e) {
    wasSso = false;
  }
  sessionStorage.removeItem(KEY);
  sessionStorage.removeItem(PKCE_KEY);

  // Clearing local storage alone leaves the hosted-UI cookie in place, so the
  // next sign-in attempt bounces straight back through the IdP and the user
  // appears to have never signed out. Tear the hosted session down too.
  const base = hostedUiBase();
  const clientId = getConfig().userPoolClientId;
  if (wasSso && base && clientId) {
    const params = new URLSearchParams({ client_id: clientId, logout_uri: appRedirectUri() });
    window.location.assign(`${base}/logout?${params.toString()}`);
  }
}

// ---------------------------------------------------------------------------
// Challenge chain.
//
// Cognito drives sign-in as a chain, not a single step, and EVERY response to a
// challenge returns a fresh Session that invalidates the one before it. So each
// call below hands the session it just received to the next call. Replaying a
// stale session comes back as NotAuthorizedException, which presents as "wrong
// password" even though the password was correct — hence the single funnel:
// whatever Cognito returns is resolved in one place, so a challenge arriving
// second (MFA_SETUP straight after NEW_PASSWORD_REQUIRED, which is exactly what
// a brand-new user hits) is handled identically to one arriving first.
//
// Hosted-UI (SSO) sign-in never reaches any of this: Entra asserts the user's
// MFA and Cognito hands back tokens at the code exchange.
// ---------------------------------------------------------------------------

function unsupportedChallenge(name) {
  const e = new Error(`This account requires a sign-in step this app cannot complete (${name}). `
    + 'Please contact an administrator.');
  e.name = 'UnsupportedChallengeException';
  return e;
}

// Cognito wants the pool's canonical username in ChallengeResponses, which in
// an alias pool is not necessarily what the user typed. It is handed back on
// the challenge itself, so prefer that and fall back to the typed address.
function challengeUsername(res, email) {
  const p = res.ChallengeParameters || {};
  return p.USER_ID_FOR_SRP || p.USERNAME || email;
}

// Resolve anything Cognito returns: tokens, or the next challenge to present.
async function resolveAuth(res, email, failMessage) {
  if (res.AuthenticationResult) return { ok: true, session: saveSession(res.AuthenticationResult) };

  const base = { session: res.Session, email, username: challengeUsername(res, email) };
  switch (res.ChallengeName) {
    case 'NEW_PASSWORD_REQUIRED':
      return { challenge: 'NEW_PASSWORD_REQUIRED', ...base };
    case 'SOFTWARE_TOKEN_MFA':
      return { challenge: 'SOFTWARE_TOKEN_MFA', ...base };
    case 'MFA_SETUP':
      return beginMfaSetup(res, email);
    case 'SELECT_MFA_TYPE': {
      // TOTP is the only factor this pool offers (SMS is deliberately off), so
      // answer for the user rather than make them pick from a list of one.
      const next = await client().send(new RespondToAuthChallengeCommand({
        ChallengeName: 'SELECT_MFA_TYPE',
        ClientId: getConfig().userPoolClientId,
        Session: res.Session,
        ChallengeResponses: { USERNAME: base.username, ANSWER: 'SOFTWARE_TOKEN_MFA' },
      }));
      // One hop only — never loop on a pool that keeps asking.
      if (next.ChallengeName === 'SELECT_MFA_TYPE') throw unsupportedChallenge(next.ChallengeName);
      return resolveAuth(next, email, failMessage);
    }
    default:
      if (res.ChallengeName) throw unsupportedChallenge(res.ChallengeName);
      throw new Error(failMessage || 'Sign-in failed');
  }
}

// Returns { ok } on success, or a { challenge, session, ... } descriptor when
// Cognito wants another step: NEW_PASSWORD_REQUIRED (temp-password flow),
// MFA_SETUP (first sign-in once MFA is required), or SOFTWARE_TOKEN_MFA.
export async function login(email, password) {
  const cfg = getConfig();
  const user = normalizeEmail(email);
  const res = await client().send(new InitiateAuthCommand({
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: cfg.userPoolClientId,
    AuthParameters: { USERNAME: user, PASSWORD: password },
  }));
  return resolveAuth(res, user, 'Login failed');
}

export async function completeNewPassword(email, newPassword, cognitoSession, username) {
  const cfg = getConfig();
  const res = await client().send(new RespondToAuthChallengeCommand({
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: cfg.userPoolClientId,
    Session: cognitoSession,
    ChallengeResponses: { USERNAME: username || email, NEW_PASSWORD: newPassword },
  }));
  // Under an MFA-required pool this is where MFA_SETUP shows up, carried on the
  // session THIS response returned — never on the one we just spent above.
  return resolveAuth(res, email, 'Could not set password');
}

// ---------------------------------------------------------------------------
// TOTP multi-factor authentication.
// ---------------------------------------------------------------------------

const MFA_ISSUER = 'Primordia';

// Standard otpauth:// URI, understood by every authenticator app. The issuer
// prefix on the label keeps the entry from showing up as a bare email address
// in a user's app. This string embeds the shared secret: it is handed to the
// UI to draw a QR code and is never logged, stored, or put in a link.
function otpauthUri(email, secret) {
  return `otpauth://totp/${MFA_ISSUER}:${encodeURIComponent(email)}`
    + `?secret=${encodeURIComponent(secret)}&issuer=${MFA_ISSUER}`;
}

// MFA_SETUP challenge: the challenge itself carries no secret, only permission
// to associate one. AssociateSoftwareToken mints it AND rotates the session, so
// the rotated session is what the caller must carry into verification.
async function beginMfaSetup(res, email) {
  const assoc = await client().send(new AssociateSoftwareTokenCommand({ Session: res.Session }));
  return {
    challenge: 'MFA_SETUP',
    session: assoc.Session,
    email,
    username: challengeUsername(res, email),
    secretCode: assoc.SecretCode,
    otpauthUri: otpauthUri(email, assoc.SecretCode),
  };
}

// Finish first-time enrolment: prove the user's app is generating the right
// codes, then answer the original MFA_SETUP challenge to complete sign-in.
// `setup` is the descriptor returned by beginMfaSetup.
export async function completeMfaSetup(code, setup) {
  const cfg = getConfig();
  const verify = await client().send(new VerifySoftwareTokenCommand({
    Session: setup.session,
    UserCode: code,
    FriendlyDeviceName: 'Primordia',
  }));
  if (verify.Status !== 'SUCCESS') {
    const e = new Error('That code was not accepted.');
    e.name = 'CodeMismatchException';
    throw e;
  }
  const res = await client().send(new RespondToAuthChallengeCommand({
    ChallengeName: 'MFA_SETUP',
    ClientId: cfg.userPoolClientId,
    // Rotated a second time by VerifySoftwareToken — setup.session is spent.
    Session: verify.Session,
    ChallengeResponses: { USERNAME: setup.username || setup.email },
  }));
  return resolveAuth(res, setup.email, 'Could not finish authenticator setup');
}

// SOFTWARE_TOKEN_MFA challenge — every sign-in after enrolment. `mfa` is the
// descriptor returned by login().
export async function completeMfa(code, mfa) {
  const res = await client().send(new RespondToAuthChallengeCommand({
    ChallengeName: 'SOFTWARE_TOKEN_MFA',
    ClientId: getConfig().userPoolClientId,
    Session: mfa.session,
    ChallengeResponses: {
      USERNAME: mfa.username || mfa.email,
      SOFTWARE_TOKEN_MFA_CODE: code,
    },
  }));
  return resolveAuth(res, mfa.email, 'Could not verify that code');
}

// Recovery goes through OUR API, not straight to Cognito, because Cognito's
// ForgotPassword refuses an account that has never completed its first sign-in
// (FORCE_CHANGE_PASSWORD) — the invited-but-lost-the-email dead end. The
// endpoint branches on the account state server-side: fresh invitation (new
// temporary password) for a never-signed-in account, the ordinary code email
// for everyone else. It answers {ok:true} for every outcome, same as this form
// reports — the enumeration silence is preserved end to end.
//
// Falls back to the direct Cognito call if the API is unreachable (an older
// deployed API without the route, or local dev without the server), so the
// worst case is exactly the behaviour this replaces.
export async function forgotPassword(email) {
  const apiUrl = (getConfig().apiUrl || '').replace(/\/+$/, '');
  if (apiUrl) {
    try {
      const res = await fetch(`${apiUrl}/auth/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizeEmail(email) }),
      });
      if (res.ok) return;
    } catch (e) {
      /* fall through to the direct call */
    }
  }
  await client().send(new ForgotPasswordCommand({
    ClientId: getConfig().userPoolClientId, Username: normalizeEmail(email),
  }));
}

export async function confirmForgotPassword(email, code, newPassword) {
  await client().send(new ConfirmForgotPasswordCommand({
    ClientId: getConfig().userPoolClientId,
    Username: normalizeEmail(email),
    ConfirmationCode: code,
    Password: newPassword,
  }));
}

// A signed-in user changing their OWN password. Authorised by the access token
// rather than by an admin call, so it works for every role and needs no
// server-side privilege: the token IS the proof of who is asking, which is why
// the current password must still be supplied — a stolen tab should not be
// enough to lock the real owner out.
export async function changeOwnPassword(previousPassword, proposedPassword) {
  const s = getSession();
  if (!s || !s.accessToken) {
    const e = new Error('Your session has expired. Please sign in again.');
    e.name = 'NotAuthorizedException';
    throw e;
  }
  await client().send(new ChangePasswordCommand({
    AccessToken: s.accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  }));
}

// ---------------------------------------------------------------------------
// Hosted UI single sign-on — authorization code + PKCE.
//
// The app client is a PUBLIC client with no secret, so the code grant is only
// safe with PKCE: the verifier never leaves this browser, and an intercepted
// code is useless without it. The implicit grant is disabled on the client on
// purpose — it would put tokens in the URL fragment, where they land in history
// and in referrers.
//
// This runs ALONGSIDE the password flow above. Local sign-in stays as the
// deliberate break-glass path for when the directory is unreachable.
// ---------------------------------------------------------------------------

// The deploy script publishes the Cognito domain into runtime config from SSM
// (/primordia/<stage>/cognito/hosted-ui-domain). If it is absent the whole SSO
// path is simply unavailable — callers hide the button rather than render one
// that cannot work.
function hostedUiBase() {
  const raw = (getConfig().hostedUiDomain || '').trim();
  if (!raw) return null;
  const host = raw.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  return host ? `https://${host}` : null;
}

// Must match a callback URL registered on the app client exactly. The client
// lists the deployed origin and http://localhost:3000, both bare origins.
function appRedirectUri() {
  return window.location.origin;
}

// A hosted-UI domain existing is NOT the same as an identity provider being
// attached to it. The domain is deployed and the whole PKCE flow is wired, but
// until a directory is actually federated there is nothing on the other end:
// pressing the button would bounce off Cognito with an unhelpful error.
//
// So a button requires a provider in runtime config, written by the deploy
// script from what AuthStack actually attached to the pool. The plumbing can
// therefore sit finished and dormant without offering anyone a control that
// cannot work. Attach a provider, redeploy, and its button appears on its own.
//
// `ssoProviders` (a list) is the current shape; `ssoProvider` (one name) is the
// original single-Entra config and is still honoured, so a stage whose
// config.json predates the list keeps working.
export function ssoProviders() {
  const cfg = getConfig();
  const list = Array.isArray(cfg.ssoProviders) ? cfg.ssoProviders : [];
  const out = list
    .filter((p) => p && typeof p.name === 'string' && p.name.trim())
    .map((p) => ({
      name: p.name.trim(),
      label: (p.label || '').trim() || `Sign in with ${p.name.trim()}`,
      // Absent means hidden. A provider that has not said it is safe to show is
      // not shown — see below.
      visible: p.visible === true,
    }));
  if (out.length) return out;
  const legacy = (cfg.ssoProvider || '').trim();
  return legacy ? [{ name: legacy, label: 'Sign in with Optimum', visible: true }] : [];
}

// The providers that get a button on the shared login page.
//
// THIS IS A TENANT-ISOLATION DECISION, NOT A COSMETIC ONE. One login page serves
// every client, so a button per federated client publishes Optimum's customer
// list to all of them — the disclosure rule 1 exists to prevent, arriving
// through the front door instead of the database. A client's provider is
// reached by the /?idp=<name> deep link handed to that client alone.
export const visibleSsoProviders = () => ssoProviders().filter((p) => p.visible);

export function ssoAvailable() {
  return !!hostedUiBase() && !!getConfig().userPoolClientId && ssoProviders().length > 0;
}

// The provider name to send to Cognito, e.g. 'EntraOptimum'. Configured, never
// guessed: the first VISIBLE provider, so a bare beginSso() cannot silently send
// a user to a client's directory.
export function ssoProviderName() {
  const shown = visibleSsoProviders();
  return shown.length ? shown[0].name : null;
}

// `?idp=<name>` on the login URL, which is how a client reaches a provider that
// has no button. Matched against the configured list rather than trusted: the
// value comes out of the address bar, and forwarding an arbitrary string to
// Cognito's identity_provider parameter is a redirect this app should not offer.
// Case-insensitive, because these get typed and pasted by hand.
export function requestedSsoProvider() {
  try {
    const asked = (new URLSearchParams(window.location.search).get('idp') || '').trim();
    if (!asked) return null;
    const match = ssoProviders().find((p) => p.name.toLowerCase() === asked.toLowerCase());
    return match ? match.name : null;
  } catch (e) {
    return null;
  }
}

// True when the current URL is a hosted-UI callback, so the UI can show the
// exchange spinner on first paint instead of flashing the password form.
export function hasSsoRedirect() {
  const q = new URLSearchParams(window.location.search);
  return q.has('code') || q.has('error');
}

function b64url(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Cryptographically random, never Math.random: the verifier is the only thing
// standing between an intercepted code and a token.
function randomToken(bytes = 32) {
  const buf = new Uint8Array(bytes);
  window.crypto.getRandomValues(buf);
  return b64url(buf);
}

async function s256Challenge(verifier) {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}

function clearPkce() {
  try {
    sessionStorage.removeItem(PKCE_KEY);
  } catch (e) {
    /* storage unavailable — nothing to clear */
  }
}

// Drop code/state/error from the address bar so a refresh cannot replay them
// and so nothing sensitive survives in history or a copied URL.
function stripSsoParams() {
  try {
    const url = new URL(window.location.href);
    ['code', 'state', 'error', 'error_description', 'error_uri'].forEach((k) => url.searchParams.delete(k));
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  } catch (e) {
    /* history API unavailable — non-fatal */
  }
}

// Cognito reports an unattached or misspelled identity provider as a generic
// invalid_request on the redirect back. Treat that family as "not wired up yet"
// rather than as a hard failure, because that is what it means in practice.
const NOT_CONFIGURED = ['invalid_request', 'unauthorized_client', 'unsupported_response_type', 'invalid_client'];
function looksUnconfigured(code, description) {
  const d = (description || '').toLowerCase();
  return NOT_CONFIGURED.includes(code) || d.includes('identity provider') || d.includes('identity_provider');
}

const SSO_NOT_CONFIGURED_MSG = 'Single sign-on is not yet configured for this environment. '
  + 'Sign in with a Primordia account below, or ask an administrator to finish the directory setup.';

// Redirect to the hosted UI for `provider` (the Cognito IdP name, e.g. Entra).
// Never resolves on success — the browser navigates away.
export async function beginSso(provider) {
  const base = hostedUiBase();
  const cfg = getConfig();
  // The provider name comes from configuration rather than a default, so this
  // can never send a user to a provider that was assumed rather than attached.
  const idp = provider || ssoProviderName();
  if (!base || !cfg.userPoolClientId || !idp) throw new Error(SSO_NOT_CONFIGURED_MSG);
  if (!window.crypto || !window.crypto.subtle) {
    throw new Error('This browser cannot perform a secure sign-in (Web Crypto unavailable over an insecure connection).');
  }

  const verifier = randomToken(32);
  const state = randomToken(16);
  const challenge = await s256Challenge(verifier);
  const redirectUri = appRedirectUri();
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state, provider, redirectUri }));

  const params = new URLSearchParams({
    identity_provider: idp,
    client_id: cfg.userPoolClientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: redirectUri,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  window.location.assign(`${base}/oauth2/authorize?${params.toString()}`);
}

async function exchangeCode(base, cfg, code, stored) {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cfg.userPoolClientId,
    code,
    redirect_uri: stored.redirectUri || appRedirectUri(),
    code_verifier: stored.verifier,
  });
  const res = await fetch(`${base}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  let data = {};
  try {
    data = await res.json();
  } catch (e) {
    data = {};
  }
  if (!res.ok || !data.id_token) {
    const err = data.error || `HTTP ${res.status}`;
    const desc = data.error_description ? `: ${data.error_description}` : '';
    return { failed: true, error: err, description: data.error_description, message: `Token exchange failed (${err}${desc}).` };
  }
  return { failed: false, data };
}

async function runSsoRedirect() {
  const q = new URLSearchParams(window.location.search);
  const code = q.get('code');
  const returnedState = q.get('state');
  const errParam = q.get('error');
  const errDesc = q.get('error_description');

  if (!code && !errParam) return { status: 'none' };

  let stored = null;
  try {
    stored = JSON.parse(sessionStorage.getItem(PKCE_KEY) || 'null');
  } catch (e) {
    stored = null;
  }

  // The IdP or Cognito rejected the request before any code was issued — most
  // often because the provider is not attached to the user pool yet.
  if (errParam) {
    stripSsoParams();
    clearPkce();
    return looksUnconfigured(errParam, errDesc)
      ? { status: 'not-configured', message: SSO_NOT_CONFIGURED_MSG }
      : { status: 'error', message: `Sign-in failed (${errParam}${errDesc ? `: ${errDesc}` : ''}).` };
  }

  // A callback with NO sign-in pending is stale, not hostile: the classic
  // source is the browser address bar autocompleting to the previous callback
  // URL (?code=...&state=... included) — Edge does this persistently, and it
  // produced an endless "state mismatch" loop for a real user. There is nothing
  // to verify against, the code is never exchanged, and the person just needs a
  // calm nudge to sign in fresh — not an alarm.
  if (!stored || !stored.state) {
    stripSsoParams();
    clearPkce();
    return {
      status: 'stale',
      message: 'That sign-in link had already been used or had expired, so it was ignored. '
        + 'Please sign in again — and avoid reopening old sign-in pages from the address bar’s suggestions or the Back button.',
    };
  }

  // CSRF guard. A pending sign-in exists and the response does not match it —
  // THIS is the genuinely suspicious case. Fail closed, never exchange the code.
  if (!returnedState || stored.state !== returnedState) {
    stripSsoParams();
    clearPkce();
    return {
      status: 'error',
      message: 'Sign-in could not be verified (state mismatch) and was stopped. Please start sign-in again from this page.',
    };
  }

  try {
    const base = hostedUiBase();
    const cfg = getConfig();
    if (!base || !cfg.userPoolClientId) return { status: 'not-configured', message: SSO_NOT_CONFIGURED_MSG };

    const out = await exchangeCode(base, cfg, code, stored);
    if (out.failed) {
      return looksUnconfigured(out.error, out.description)
        ? { status: 'not-configured', message: SSO_NOT_CONFIGURED_MSG }
        : { status: 'error', message: out.message };
    }

    // Same persistence path as the password flow, so nothing downstream can
    // tell a federated session from a local one.
    const session = saveSession({
      IdToken: out.data.id_token,
      AccessToken: out.data.access_token,
      RefreshToken: out.data.refresh_token,
    }, { sso: true });

    return {
      status: 'signed-in',
      session,
      needsAssignment: needsWorkspaceAssignment(session),
    };
  } catch (e) {
    return { status: 'error', message: e.message || 'Sign-in failed.' };
  } finally {
    // Every exit path: no replayable code in the URL, no verifier left behind.
    stripSsoParams();
    clearPkce();
  }
}

// Memoised so a double mount (React StrictMode) cannot try to redeem the same
// one-time code twice.
let ssoRedirect = null;
export function completeSsoRedirect() {
  if (!ssoRedirect) ssoRedirect = runSsoRedirect();
  return ssoRedirect;
}
